import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

/**
 * Custom hook to debounce a value. The debounced value will only update after the specified delay has passed without any changes.
 *
 * @param value - The value to debounce.
 * @param [delay=1000] - The delay in milliseconds to wait before updating the debounced value.
 *
 * @returns The debounced value.
 *
 * @example
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     // Perform search
 *   }
 * }, [debouncedSearchTerm]);
 */

export function useDebounce<T>(value: T, delay: number = 1000): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
